import { useTranslation } from 'react-i18next';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import DesktopSplit from '../components/DesktopSplit';
import { SHOW_URL } from '../components/Footer';
import { useFlow } from '../state/flow';
import { WelcomeRight } from './Welcome';
import { ShareRight } from './Share';
import './WelcomeShareFlow.scss';

/**
 * Stable layout shared by /welcome and /share. The headline + subtitle live
 * in the left pane and stay put across the navigation; the right pane runs a
 * sequential fade-out → fade-in (not crossfade) between the card preview and
 * the share tiles. The CTA wrapper stays at full height across both routes —
 * the Continue button on /welcome fades out and the Start Over / Watch Off
 * Campus pair fades in on /share, occupying the same footprint.
 */
export default function WelcomeShareFlow() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // Plain navigate (no view transition) so the header / footer / `.app`
  // root never crossfade on the /welcome ↔ /share hop. The right-pane
  // sequential fade handles the only thing that should change visually.
  const navigate = useNavigate();
  const { hasFormData, hasPhoto } = useFlow();
  const isShare = pathname === '/share';

  if (!hasFormData || !hasPhoto) {
    return <Navigate to="/" replace />;
  }

  // Photo state is cleared on /id-card via `location.state.resetPhoto`, not
  // here — clearing synchronously would flip `hasPhoto` to false before the
  // navigation commits and the guard above would fire `<Navigate to="/" />`,
  // bouncing the user through the home preloader before reaching /id-card.
  function handleStartOver() {
    navigate('/id-card', { state: { resetPhoto: true } });
  }

  return (
    <DesktopSplit
      left={
        <div className="ws-left">
          <h1 className="ws-left__title">{t('welcome.title')}</h1>
          <p className="ws-left__subtitle">{t('welcome.subtitle')}</p>
          <div className="ws-left__cta">
            <div
              className={`ws-left__cta-layer${!isShare ? ' is-active' : ''}`}
              aria-hidden={isShare}
            >
              <button
                type="button"
                className="ws-left__cta-btn"
                onClick={() => navigate('/share')}
                tabIndex={isShare ? -1 : 0}
              >
                {t('common.continue')} →
              </button>
            </div>
            <div
              className={`ws-left__cta-layer ws-left__cta-layer--dual${isShare ? ' is-active' : ''}`}
              aria-hidden={!isShare}
            >
              <button
                type="button"
                className="ws-left__cta-btn"
                onClick={handleStartOver}
                tabIndex={isShare ? 0 : -1}
              >
                {t('common.startOver')}
              </button>
              <a
                className="ws-left__cta-btn"
                href={SHOW_URL}
                target="_blank"
                rel="noreferrer"
                tabIndex={isShare ? 0 : -1}
              >
                {t('common.watchOffCampus')}
              </a>
            </div>
          </div>
        </div>
      }
      right={
        <div className="ws-right">
          <div
            className={`ws-right__layer${!isShare ? ' is-active' : ''}`}
            aria-hidden={isShare}
          >
            <WelcomeRight />
          </div>
          <div
            className={`ws-right__layer${isShare ? ' is-active' : ''}`}
            aria-hidden={!isShare}
          >
            <ShareRight />
          </div>
        </div>
      }
    />
  );
}
